/**
 * useCampaignProductEdit composable
 *
 * Owns the delete + stock-availability dialog infrastructure for a
 * campaign product, lifted out of `StoreProducts/index.vue` so any
 * surface that renders a `<product-card>` can let the user delete it or
 * inspect stock.
 *
 * Always paired with `<product-edit-host>`. The host owns the template
 * ref for StockAvailabilityDialog and passes it in as a plain getter
 * (NOT a ref across a prop boundary) so we never trip Vue 3's
 * prop-reactive auto-unwrap.
 *
 * Architecture notes (re-attempt 2026-05-08):
 *   - This composable is ONLY used inside ProductEditHost.vue. Consumers
 *     interact with the host's `defineExpose` API, not with this
 *     composable directly. That keeps every ref internal to the host's
 *     setup() scope.
 *   - The dialog "ref" the legacy code mutated via `this.$refs.foo` is
 *     now passed in as `getStockDialogInstance` -- a plain function. The
 *     host wires it to its own template ref. No ref crosses a component
 *     boundary.
 *
 * Scope note (2026-09-01): the legacy ProductCreationModal stack and its
 * two ordered-product dialogs were removed in the dead-code sweep, and
 * with them this composable's `editProduct` / `handleProductSave` /
 * ordered-dialog plumbing and the percent<->fraction margin conversion
 * that existed only for the modal's persistence boundary. Product
 * editing now routes to `/distributor/product-editor/:id`.
 *
 * Inputs:
 *   - campaign        Reactive ref to the campaign object (must have
 *                     `_jv.id`).
 *   - campaignProducts Reactive ref to the list of campaign products.
 *                     Mutated in place to keep reactivity.
 *   - getStockDialogInstance   () => StockAvailabilityDialog instance | null
 *   - onCampaignChanged Optional callback invoked with the updated
 *                       campaignProducts list after delete.
 */
import { ref } from 'vue'
import { useStore } from 'vuex'
import axiosInstance from '@/axios'
import { describeError } from "@/utils/safeErrorLog";

/**
 * Build the delete endpoint URL for a campaign product.
 *
 * A stub campaign product (created by the Products-tab "Create Product"
 * CTA before the editor has bound a catalog product) has a null
 * `product_id`; the endpoint expects an integer in that path segment, so
 * substitute 0 rather than emitting "null".
 *
 * Exported via `_internal` for behavioral tests.
 */
function buildDeleteProductUrl(campaignProduct) {
    const productId = campaignProduct?.product_id ?? 0
    return `/product/deleteProduct/${campaignProduct.id}/${productId}`
}

export function useCampaignProductEdit({
  // `campaign` and `onProductUpload` are accepted but unused since the
  // 2026-09-01 sweep removed the modal edit/save flow that read them.
  // They stay in the signature so ProductEditHost's call site -- and the
  // live `product-upload` event chain it forwards to -- keep working
  // unchanged; retiring them is a follow-up on the host side.
  // eslint-disable-next-line no-unused-vars
  campaign,
  campaignProducts,
  getStockDialogInstance = () => null,
  onCampaignChanged = null,
  // eslint-disable-next-line no-unused-vars
  onProductUpload = null,
} = {}) {
  const store = useStore()

  // ---------- reactive state owned by the composable ----------
  const deleteProductDialogOpened = ref(false)
  const deleteProductPending = ref(false)
  const deleteCampaignProduct = ref({})

  const stockMessage = ref(null)
  const stockInfo = ref(null)
  const selectedStockCampaignProduct = ref({ product_name: '' })

  // ---------- helpers ----------
  const getProducts = () => {
    if (campaignProducts && 'value' in campaignProducts) return campaignProducts.value
    return campaignProducts
  }

  const emitCampaignChanged = () => {
    if (typeof onCampaignChanged === 'function') {
      onCampaignChanged(getProducts())
    }
  }

  // ---------- delete flow ----------

  const promptDeleteProduct = (campaignProduct) => {
    deleteCampaignProduct.value = campaignProduct
    deleteProductDialogOpened.value = true
  }

  const setDeleteDialogOpen = (val) => {
    deleteProductDialogOpened.value = !!val
  }

  const actuallyDeleteProduct = async (campaignProduct) => {
    deleteProductPending.value = true

    let products = getProducts()
    if (!products) {
      products = []
    }

    let matchingIndex = -1
    for (const index in products) {
      if (products[index].id === campaignProduct.id) {
        matchingIndex = index
        break
      }
    }

    if (matchingIndex > -1) {
      products.splice(matchingIndex, 1)
    }

    deleteProductPending.value = false
    deleteProductDialogOpened.value = false
    deleteCampaignProduct.value = {}

    try {
      await axiosInstance.get(buildDeleteProductUrl(campaignProduct))
    } catch (err) {
      console.log(describeError(err))
      store.dispatch('raiseError', err)
    }

    if (products && products.length === 0) {
      store.commit('setDistributorStatus', false)
    }

    emitCampaignChanged()
  }

  // ---------- stock-availability ----------

  const showStockDialog = async (campaignProduct, info) => {
    stockInfo.value = info
    selectedStockCampaignProduct.value = campaignProduct
    const dlg = getStockDialogInstance()
    if (dlg) {
      dlg.show()
    }
  }

  return {
    // reactive state — these refs stay INSIDE the host component scope.
    // They never cross a prop boundary.
    deleteProductDialogOpened,
    deleteProductPending,
    deleteCampaignProduct,
    stockMessage,
    stockInfo,
    selectedStockCampaignProduct,

    // methods
    promptDeleteProduct,
    setDeleteDialogOpen,
    actuallyDeleteProduct,
    showStockDialog,
  }
}

// Behavioral testing surface: the pure URL helper, for tests that do not
// need a full Vuex harness. The composable factory remains the public API.
export const _internal = {
  buildDeleteProductUrl,
}

export default useCampaignProductEdit
